import { z } from 'zod';
/**
 * Plans, tiers and features — which capabilities somebody has, and why
 * (`docs/plans-and-features.md`).
 *
 * Three ideas, and nothing else:
 *
 *  - a **plan** is what a team is: `free` or `paid`. People pay per team, so a paid feature is
 *    the team's, and it is the same for everybody in that team and for every game the team owns;
 *  - a **tier** is what a person is: `free`, `beta` or `alpha`. A person joins and leaves the beta
 *    themselves, from their account; only an operator puts somebody in the alpha. Each includes
 *    the one below it, so somebody in the alpha is in the beta too;
 *  - a **feature** is one capability, with one tier saying who reaches it: nobody (`off`), a person
 *    at least that far in (`alpha`, `beta`), a paid team (`paid`) or everybody (`free`). A feature
 *    is released by moving it along that line in the admin console — off, alpha, beta, then free or
 *    paid — which writes `feature_tiers` (migration 0088). A feature with no row there is off, so a
 *    key added to this file ships to nobody until an operator releases it.
 *
 * There is no per-feature access. Nobody is given one feature on its own, and nobody is switched off
 * one: what a team has follows from its plan and what a person has follows from their tier.
 *
 * The word "entitlement" is deliberately not used anywhere here. It already means something
 * else in this system — which *apps* a person may install (plan §5, `app_entitlements`) — and
 * two meanings for one word in one codebase is how a `resolveEntitlements` gets called from the
 * wrong place. Features gate what a person may *do*; entitlements gate what they may *install*.
 *
 * ## The plan is invisible to a user
 *
 * There is no upgrade path, no price, no "upgrade to unlock" and no plan shown anywhere in any
 * client. A plan is set by an operator, and that is the entire product surface of it. That is why
 * the API ships the *enabled* feature keys and never the disabled ones (see `Team.features`): a free
 * team's response is indistinguishable from one where the paid feature does not exist at all, which
 * is the point. A UI that hides a control it does not have the feature for tells the user nothing; a
 * UI that greys one out and says why tells them everything.
 *
 * This is the one place in the dashboard that overrides the standing rule that role-gated
 * controls stay visible and disabled. That rule is about *permission* — you may not do this,
 * here is who can. This is about a capability whose existence is not being advertised.
 *
 * A tier is not hidden from the person who holds it — they chose the beta, and are told they are in
 * the alpha — but which features a tier brings is: the account page offers the beta, never a list
 * of what is in it, so a Paid or Alpha feature is named nowhere a person without it can read.
 */
/** What a team is. People pay per team. */
export const Plan = z.enum(['free', 'paid']);
/** What a person is. Each includes the one below it. */
export const Tier = z.enum(['free', 'beta', 'alpha']);
/** Higher reaches more. */
export const TIER_RANK = { free: 0, beta: 1, alpha: 2 };
/**
 * The tier a person may put themselves in from their account (`PATCH /v1/me`). The alpha is not one
 * of them: only an operator puts somebody there, and a person in it leaves by choosing one of these.
 */
export const SelfTier = z.enum(['free', 'beta']);
/**
 * Who reaches a feature, in the order a feature is released: nobody, the alpha, the beta, then a
 * paid team or everybody. `off` is what a feature with no row in `feature_tiers` is.
 */
export const FeatureTier = z.enum(['off', 'alpha', 'beta', 'paid', 'free']);
/**
 * Every gate in the system, by key.
 *
 * A key is a permanent name. It appears in a Worker's `requireFeature` call, in the dashboard's
 * `useFeature` hook, in the `features:` lines of the assistant's portal map and in `feature_tiers`,
 * where renaming one would turn it off for everybody. Add keys; do not rename them.
 */
export const FeatureKey = z.enum([
    'playground',
    'playground.pixi',
    'playground.boardjs',
    'playground.rewrite',
    'playground.external',
    'playground.claudecode',
    'playground.mcpkeys',
    'playground.fileeditor',
    'playground.teamai',
    'assets.library',
    'feedback.layer',
    'analytics',
    'analytics.events',
    'play.services',
    'signing.custom',
    'verification',
    'tokens.api',
    'install.byhand',
    'assistant',
    'profiles',
]);
/**
 * The features. Who reaches each is not here: it is `feature_tiers`, set in the admin console, and a
 * key with no row there is off. Migration 0088 seeded it from what the old `features` table said:
 * four keys `paid` — `playground.rewrite`, `playground.mcpkeys`, `playground.fileeditor` and
 * `playground.teamai` — six `alpha` — `playground.external`, `playground.claudecode`,
 * `analytics.events`, `play.services`, `install.byhand` and `assistant` — and every other key `free`.
 * A free key is a key because it is worth being able to move behind a tier from the console, not
 * because it is being held back. Three reasons a capability earns a key:
 *
 *  1. **It spends BoardFlight's money on somebody else's behalf.** A playground is an E2B
 *     sandbox by the hour; the feedback layer is a second sandbox and two moves of a multi-GB
 *     artifact per build; the asset library is R2 bytes a stranger uploaded. `teams.playground_
 *     seats` already bounds *how many* sandboxes; this bounds *whether*.
 *  2. **It is a scarce physical resource.** Hardware verification runs on a real Board, and
 *     there is one.
 *  3. **It is a decision about the outside world.** A custom signing key is the one moment a team
 *     hands this system a real secret.
 *
 * What deliberately has **no** key: publishing, channels, testers, redeem codes, invites,
 * install activity and the library itself. Those are what BoardFlight is. A switch that could
 * turn one of them off for somebody is a switch that will eventually be flipped by accident,
 * and the feature it protects is not worth that.
 *
 * `store.public` was the one key this list was *waiting* for. The store shipped without it: what
 * holds a game back from strangers is an operator approving its first trip to the store, per game,
 * not a switch per team. A key with no enforcement behind it is worse than no key, because the
 * dashboard would hide a control the API still allows; `docs/plans-and-features.md` §4 records
 * why.
 */
export const FEATURES = [
    {
        key: 'playground',
        summary: 'Open a playground — the browser workspace backed by an E2B sandbox.',
        effect: 'On: anybody with developer rights in the team can attach a playground to a web game and ' +
            'start a session — a real E2B sandbox, billed by the second, bounded by the team\'s ' +
            'sandbox seats and started at the team\'s container size. Off: attaching one and starting ' +
            'a session both 404, and so does reconnecting to a sandbox that is already running, so a ' +
            'tab left open is not a way round it. Nothing is deleted — the project\'s files stay in R2 ' +
            'and come back with the next session.',
    },
    {
        key: 'playground.pixi',
        summary: 'Create a playground on the PixiJS engine rather than canvas.',
        effect: 'On: a new playground may be created on PixiJS, and the dashboard offers the choice. Off: ' +
            'the choice is hidden and canvas is what a new playground gets. Playgrounds already on ' +
            'pixi are unaffected — the engine is fixed when the playground is created and never asked ' +
            'about again — so this holds back a pixi regression without stopping anybody working.',
    },
    {
        key: 'playground.boardjs',
        summary: 'Start a new PixiJS playground on BoardJS.',
        effect: 'On: a new playground on PixiJS starts from the BoardJS starter, the same game as ' +
            '`scene.json`, `tuning.json` and `game.ts`, and the engine card says so. Off: a new PixiJS ' +
            'playground gets the plain PixiJS starter, and asking for the BoardJS one 404s. Which ' +
            'harness a playground works under is never gated: it follows the project\'s own ' +
            '`package.json`, so a project already on BoardJS keeps the BoardJS harness whoever opens it.',
    },
    {
        key: 'playground.rewrite',
        summary: 'Rewrite a canvas game in PixiJS — clone it into a new app on the new engine.',
        effect: 'On: a canvas game can be cloned into a **second app** on PixiJS — a new row in `apps` ' +
            'with its own package id, which goes through the same §4 prefix check every app creation ' +
            'does, plus a copy of the project and optionally of the asset library. Off: asking for a ' +
            'rewrite 404s. The two routes that *finish* one already in flight are deliberately not ' +
            'gated, so a team leaving the paid plan cannot strand a half-copied game.',
    },
    {
        key: 'playground.external',
        summary: "Link a playground to the developer's own agent on their computer instead of the sandbox's assistant.",
        effect: 'On: a playground may be switched to the developer\'s own agent (Claude Code or Codex on their ' +
            'computer), which `boardflight playground link` keeps in step with the sandbox; the ' +
            'sandbox then runs no assistant and holds no ChatGPT credential. Asked of the person ' +
            'switching, and of the person who starts each session. Off: switching a playground to it ' +
            '404s, and one already switched starts its next sandbox with the assistant, saying nothing ' +
            'about why. A sandbox that is linked right now keeps running until it ends; the change ' +
            'takes effect at the next fresh start.',
    },
    {
        key: 'playground.claudecode',
        summary: 'Work on a playground with Claude Code, in a terminal in its sandbox.',
        effect: 'On: the first-run pane offers "Connect Claude Code", a playground may be switched to it, and ' +
            'that playground\'s sandboxes start on the Claude Code image — at least `large`, a bigger ' +
            'machine than the ChatGPT one — with Claude Code in a terminal the developer signs in to ' +
            'with their own account. Asked of the person switching, and of the person who starts each ' +
            'session. Off: switching a playground to it 404s, and one already switched starts its next ' +
            'sandbox on the ChatGPT image, saying nothing about why. A Claude Code sandbox running right ' +
            'now keeps running until it ends. Put nobody in the alpha before the Claude Code templates ' +
            'are published on this deployment, because the pane offers the option on this key alone.',
    },
    {
        key: 'playground.mcpkeys',
        summary: "Hold API keys for MCP servers that every developer on the team can use in the team's playgrounds.",
        effect: 'On: a team admin may add a key for any catalogue MCP server that takes one, and a developer ' +
            'can choose it for that server in any of the team\'s playgrounds instead of typing their own; ' +
            'the sandbox is handed it when it starts or saves its servers. Off: adding or replacing a key ' +
            '404s, and a sandbox asking for one is refused, so a server using it is left out with a ' +
            'sentence on its row. Listing and removing stay open, because a team that has left the paid ' +
            'plan must still be able to see and delete the secrets it gave BoardFlight. A sandbox that ' +
            'already has a key keeps it until it next applies its list.',
    },
    {
        key: 'playground.fileeditor',
        summary: "Browse a playground's files and read them in its Files window.",
        effect: 'On: the playground offers a Files window — the project as a tree, and any file in it ' +
            'shown read-only with its syntax coloured — and a sandbox started for the team lists its ' +
            'files to it. Off: the window is not offered, and a sandbox started without the feature ' +
            'refuses to list the project. It does not hide the files themselves: the assistant and ' +
            'the terminal read every one, and the inspector still reads and writes `src/layout.json` ' +
            'by name. Decided when a session starts, so a sandbox already running keeps what it ' +
            'started with until it ends.',
    },
    {
        key: 'playground.teamai',
        summary: "Run the team's playgrounds on an AI provider key the team holds, instead of each developer's ChatGPT.",
        effect: 'On: a team admin may give the team an OpenRouter management key or a Cheaper Inference key ' +
            'with a monthly cap and a choice of models, and a sandbox started for the team runs its ' +
            'assistant on it by default — an OpenRouter session key minted for that sandbox alone, or ' +
            'the Cheaper Inference key itself. Off: giving or replacing a key 404s, and a sandbox asking ' +
            'for one is refused, so the assistant asks for a ChatGPT sign-in as it always did. Listing, ' +
            'changing the cap or models and removing stay open, because a team that has left the paid ' +
            'plan must still be able to see and delete the secret it gave BoardFlight. A sandbox already ' +
            'holding a session key keeps it until its session ends (docs/playground-team-ai-plan.md).',
    },
    {
        key: 'assets.library',
        summary: "Upload to a game's asset library.",
        effect: 'On: new bytes may be uploaded to a game\'s asset library. Off: only that one door closes ' +
            '— listing, renaming, moving and deleting still work, and every asset already there is ' +
            'still served to sandboxes and to builds. The library is somebody\'s work, and a switch ' +
            'that made it unreachable rather than read-only would be a way to lose it.',
    },
    {
        key: 'feedback.layer',
        summary: 'Turn on the in-game feedback layer, which patches and re-signs every build.',
        effect: 'On: a channel may be switched to patch every build it publishes — unpack, add the layer, ' +
            're-sign, re-check the package id, publish. That is a second E2B sandbox and two moves of ' +
            'a multi-GB artifact per build, and the re-signing costs testers one uninstall the first ' +
            'time. Off: only turning the layer **on** is refused. A team that already has it on can ' +
            'still turn it off and still restyle the button, because a gate on the route would be a ' +
            'gate on turning it off.',
    },
    {
        key: 'analytics',
        summary: 'Collect and read game analytics.',
        effect: 'On: collection may be switched on for a game or one of its channels. Off: only switching ' +
            'it **on** is refused — a channel that is already collecting goes on collecting, its ' +
            'settings can still be switched off, and every figure already gathered stays readable. ' +
            'Taking collection away is two steps: moving this key, and turning the existing channels off.',
    },
    {
        key: 'analytics.events',
        summary: 'Collect and read custom game events.',
        effect: 'On: a channel may be switched to collect the events the game\'s own code reports, which ' +
            'turns on play sessions with it and so patches and re-signs its builds; the game\'s event ' +
            'catalog and its reports can be read and written. Off: switching events **on** and every ' +
            'catalog and report route 404, and the dashboard hides the switch and the catalog. A ' +
            'channel already collecting goes on collecting, because a game reporting an event has no ' +
            'person to ask; switching the channel off is what stops it. Nothing stored is deleted.',
    },
    {
        key: 'play.services',
        summary: "Achievements, leaderboards and player profiles for a team's games.",
        effect: "On: the game's achievements and leaderboards catalog and its icons can be read and written, " +
            'and the dashboard shows Settings › Achievements, Settings › Leaderboards and the channel ' +
            'switches. Off: every catalog route 404s and the dashboard hides those pages. A channel ' +
            'already switched on goes on answering its game, because a game reporting an unlock has no ' +
            'person to ask; switching the channel off is what stops it. Nothing stored is deleted, and ' +
            'nothing a player earned is taken back (docs/achievements-plan.md §8.5).',
    },
    {
        key: 'signing.custom',
        summary: "Sign patched builds with the team's own uploaded keystore.",
        effect: 'On: the team may upload its own keystore for a game and register it, and every patched ' +
            'build of that game is signed with it — which is what makes a patched build ' +
            'indistinguishable from an unpatched one to a tester. Off: both halves of bringing a key ' +
            'in 404. A key BoardFlight generated for the game is untouched and cannot be gated (the ' +
            'patcher has to sign with something), and removing a key stays open, because taking your ' +
            'own secret back out is not a capability anybody should have to hold.',
    },
    {
        key: 'verification',
        summary: 'Queue a build for verification on real Board hardware.',
        effect: 'On: a finished build may be queued by hand for a run on real Board hardware, of which ' +
            'there is one. Off: only asking by hand is refused — a game with verify-on-publish set ' +
            'still queues from inside the publish path, because a publish that failed on a switch ' +
            'moving would be a far worse surprise than a check that quietly still runs.',
    },
    {
        key: 'tokens.api',
        summary: 'Mint `bft_…` API tokens for CI.',
        effect: 'On: a team admin may mint `bft_…` tokens for CI. Off: only minting. Listing and revoking ' +
            'stay open and tokens already minted keep working until they expire or are revoked — a ' +
            'gate that stranded live credentials outside their owner\'s reach would be worse than no ' +
            'gate. The playground\'s own deploy credential is minted elsewhere and is unaffected.',
    },
    {
        key: 'install.byhand',
        summary: "Install a game without the desktop app — download its file and drop it on the Board's own page.",
        effect: 'Asked of the person alone, with no team in the question. On: the library offers that ' +
            'person the by-hand install — download a game\'s file, then drop it on the page the Board ' +
            'itself serves — and the API hands them the file. Off, which is everybody outside the ' +
            'alpha: the library offers the desktop app and nothing else, and a build\'s download is ' +
            'refused to a caller who is neither a developer on the game\'s team nor the desktop app. A ' +
            'developer\'s download from their own game\'s Builds page, an API token\'s, and every ' +
            'install the desktop app makes are untouched. Nothing is removed from anybody\'s My Games.',
    },
    {
        key: 'assistant',
        summary: "Ask the assistant on the creators' dashboard.",
        effect: 'Asked of the person alone, with no team in the question: the first inference BoardFlight ' +
            'pays for itself (docs/assistant-plan.md §0.6). On: the creators\' dashboard shows Ask, and ' +
            'the assistant answers from its knowledge pack and reads that person\'s own games through a ' +
            'read-only token. Off: no button, no panel, and every assistant route answers the same 404. ' +
            'Conversations an account already had are kept to the end of their 30 days, unread.',
    },
    {
        key: 'profiles',
        summary: "A player's own profile in the library, and adding a game's Board player to it.",
        effect: 'Asked of the person alone, with no team in the question (docs/achievements-plan.md §4.3, ' +
            '§12a.14). On: the library shows that person Profile, /u/me answers, and /keep/<code> lets ' +
            'them add a Board player of a game to their account. Off: no Profile in the library, /u/me ' +
            'and the confirm route answer the same 404, and a game asking for a link still gets its code, ' +
            'because a game is nobody and cannot be asked about a tier. A public profile at /u/<name> ' +
            'answers only while its owner has the key, so turning it off hides every profile it opened. ' +
            'Nothing stored is deleted: links and the unlocks they brought stay, unread, until it is on again.',
    },
];
export const FEATURE_KEYS = FEATURES.map((feature) => feature.key);
/** The definition of one key, or `null` for a string that is not a feature. */
export function featureDefinition(key) {
    return FEATURES.find((feature) => feature.key === key) ?? null;
}
/**
 * Whether one feature is on, for one person acting in one team.
 *
 * The whole design, in one place, so the Worker, the admin console and the dashboard's tests all
 * ask it rather than reimplementing it:
 *
 *  - an `off` feature is off, for everybody;
 *  - a `free` feature is on;
 *  - a `paid` feature is on when the team in the question is paid. `plan` is null when there is no
 *    team in the question — a person asking for themselves — and a paid feature is then off;
 *  - a `beta` or `alpha` feature is on when the person is at least that far in. `tier` is null when
 *    there is no person in the question, and only the free tier's features are then on.
 *
 * Nothing else is an input. There is no grant to consult and no order of precedence to get wrong.
 */
export function resolveFeature(input) {
    switch (input.tier) {
        case 'off':
            return false;
        case 'free':
            return true;
        case 'paid':
            return input.plan === 'paid';
        default:
            return TIER_RANK[input.userTier ?? 'free'] >= TIER_RANK[input.tier];
    }
}
/** Every key that is on for this pair, given each feature's tier — `resolveFeature` over the registry. */
export function featuresOn(tiers, plan, userTier) {
    return FEATURES.filter((feature) => resolveFeature({ tier: tiers[feature.key] ?? 'off', plan, userTier })).map((feature) => feature.key);
}
//# sourceMappingURL=features.js.map